import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, catchError, throwError } from 'rxjs';
import { SessionService } from './session.service';

/**
 * Auth0-SDK error codes that mean "silent token acquisition failed and the
 * user needs to interactively re-authenticate". The Angular Auth0 SDK's
 * AuthHttpInterceptor throws these BEFORE the HTTP request goes out when
 * `getAccessTokenSilently()` fails — so they never surface as an
 * `HttpErrorResponse` with status 401 and would otherwise slip past this
 * interceptor's classic 401 check. Matched against the `error` property
 * (auth0-spa-js OAuth error codes) OR the class name (SDK error subclasses).
 * Kept in a Set for O(1) match and easy extension.
 */
const AUTH0_EXPIRY_ERROR_CODES = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'missing_token',
  'invalid_grant',
  'token_expired',
]);

const AUTH0_EXPIRY_ERROR_NAMES = new Set([
  'MissingRefreshTokenError',
  'GenericError',        // auth0-spa-js base — inspect .error property too
  'AuthenticationError',
  'TokenError',
]);

/**
 * Return true if `err` looks like a session-expiry signal that ought to
 * flip SessionService into the expired state. Covers:
 *   1. HttpErrorResponse with status 401 (the API rejected our token).
 *   2. Auth0 SDK errors thrown from `getAccessTokenSilently` when the
 *      refresh chain has ended (login_required, missing_refresh_token,
 *      etc.) — these never travel as an HttpErrorResponse and used to
 *      slip past the interceptor, leaving the dialog stranded.
 * Non-expiry errors return false and are re-thrown untouched.
 */
function looksLikeSessionExpiry(err: unknown): boolean {
  if (err instanceof HttpErrorResponse && err.status === 401) return true;
  if (err && typeof err === 'object') {
    const e = err as { error?: unknown; name?: string; constructor?: { name?: string } };
    if (typeof e.error === 'string' && AUTH0_EXPIRY_ERROR_CODES.has(e.error)) return true;
    if (typeof e.name === 'string' && AUTH0_EXPIRY_ERROR_NAMES.has(e.name)) return true;
    const ctorName = e.constructor?.name;
    if (typeof ctorName === 'string' && AUTH0_EXPIRY_ERROR_NAMES.has(ctorName)) return true;
  }
  return false;
}

/**
 * Reactive safety net for the SessionService's proactive expiry watcher.
 *
 * When the API returns 401 OR the Auth0 SDK fails to acquire a token
 * silently, the user's session is effectively over. We DON'T drive the
 * Auth0 redirect from here — that's SessionService.reauthenticate(),
 * gated behind an explicit user click on the expired dialog so the
 * user isn't yanked mid-typing.
 *
 * Instead:
 *   1. Flag the session expired via SessionService.markExpired() — idempotent,
 *      so a burst of parallel 401s produces exactly ONE dialog, ONE redirect.
 *   2. Return `EMPTY` for the failing request so downstream `catchError`
 *      handlers in feature code (there are ~8 "Save failed / Please try
 *      again" toasts across the codebase) don't fire. The dialog is the
 *      user-facing signal now; the vague toasts would only confuse.
 *
 * Non-expiry errors pass through untouched.
 */
export const authExpiryInterceptor: HttpInterceptorFn = (req, next) => {
  const session = inject(SessionService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (looksLikeSessionExpiry(err)) {
        // Log the specific shape so a future stale error signature (Auth0
        // renames a code, adds a new SDK error class, etc.) is diagnosable
        // from console noise rather than "the dialog just stopped working".
        const status = err instanceof HttpErrorResponse ? `http-${err.status}` : 'auth0-sdk';
        const code   = (err as { error?: string })?.error ?? (err as { name?: string })?.name ?? 'unknown';
        session.markExpired(`${status}:${code}:${req.method} ${req.url}`);
        session.note401Swallowed();
        // Swallow the error — the dialog is the UX signal, so downstream
        // `catchError` handlers (which would each show a vague toast)
        // must not fire.
        //
        // EMPTY (not NEVER) so that operators wired downstream still get
        // to complete: `finalize` cleanup runs (loading spinners clear),
        // `takeUntil` teardown fires, `firstValueFrom` rejects with
        // EmptyError instead of hanging forever. NEVER would keep those
        // subscriptions live indefinitely, leaking any resources they
        // hold until the SPA unloads at re-auth.
        //
        // Trade-off: components using .subscribe(next, err) see NEITHER
        // next NOR err — they see completion. Any per-request success
        // handler simply doesn't run, which is exactly what we want
        // (the response never came back).
        return EMPTY;
      }
      return throwError(() => err);
    }),
  );
};
